import {
  DataTable,
  ExpirationStatusBadge,
  FileIcon,
  TableMenu,
} from '@/components'
import type { TableMenuItem } from '@/components'
import { cn } from '@/lib/utils'
import type { VehicleDocumentNode } from '@/modules/vehicles'
import { getExpirationStatus } from '@/modules/common/expiration-status'
import { DynamicTable } from '@/modules/dynamic_tables'
import {
  MENU_COLUMN_ID,
  resolveVehicleColumns,
  useVehicleColumnLabels,
  useVehicleTableColumns,
  VehicleColumn,
} from '@/modules/vehicles/table-config'
import { PERMISSIONS, usePermissions } from '@/utils'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'

interface Props {
  documents: VehicleDocumentNode[]
  loading?: boolean
  /** Off on a single vehicle's own tab, where every row is that vehicle. */
  showVehicle?: boolean
  onEdit(document: VehicleDocumentNode): void
  onDelete(document: VehicleDocumentNode): void
}

export const VehicleDocumentsTable: React.FC<Props> = ({
  documents,
  loading = false,
  showVehicle = true,
  onEdit,
  onDelete,
}) => {
  const translations = useTranslate({
    noFiles: 'documents.no-files',
    openFile: 'documents.open-file',
    edit: 'common.edit',
    delete: 'common.delete',
    items: ['vehicles.documents.num-documents', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicledocument,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicledocument,
    true
  )

  const { config, loading: configLoading } = useVehicleTableColumns(
    DynamicTable.VEHICLE_DOCUMENTS
  )
  const labels = useVehicleColumnLabels()[DynamicTable.VEHICLE_DOCUMENTS]

  const column = createColumnHelper<VehicleDocumentNode>()

  const columnDefs = useMemo(() => {
    const defs = [
      column.accessor('vehicle', {
        id: VehicleColumn.VEHICLE,
        header: labels[VehicleColumn.VEHICLE],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.vehicle?.registrationNumber ?? '').localeCompare(
            b.original.vehicle?.registrationNumber ?? ''
          ),
        cell(info) {
          const vehicle = info.getValue()
          if (!vehicle) return '-'

          return (
            <Link className="text-inherit" to={`/vehicle-fleet/${vehicle.id}`}>
              {vehicle.registrationNumber || '-'}
            </Link>
          )
        },
      }),
      column.accessor('name', {
        id: VehicleColumn.NAME,
        header: labels[VehicleColumn.NAME],
        enableSorting: true,
        cell(info) {
          const document = info.row.original

          return (
            <a
              className="inline-flex items-center gap-2 text-inherit"
              href={document.file}
              target="_blank"
              rel="noreferrer"
              title={translations.openFile}
            >
              <FileIcon
                file={{ name: document.originalName, url: document.file }}
              />
              {info.getValue()}
            </a>
          )
        },
      }),
      column.accessor('description', {
        id: VehicleColumn.DESCRIPTION,
        header: labels[VehicleColumn.DESCRIPTION],
        cell: info => info.getValue() || '-',
      }),
      column.accessor('expirationDate', {
        id: VehicleColumn.EXPIRATION_DATE,
        header: labels[VehicleColumn.EXPIRATION_DATE],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) => {
          const aDate = a.original.expirationDate
          const bDate = b.original.expirationDate
          // Documents without an expiration date sort last.
          if (!aDate) return bDate ? 1 : 0
          if (!bDate) return -1
          return new Date(aDate).getTime() - new Date(bDate).getTime()
        },
        cell(info) {
          const expirationDate = info.getValue()
          if (!expirationDate) return '-'
          return format(new Date(expirationDate), 'PP')
        },
      }),
      column.display({
        id: VehicleColumn.STATUS,
        header: labels[VehicleColumn.STATUS],
        meta: { narrow: true },
        cell(info) {
          const status = getExpirationStatus(info.row.original.expirationDate)
          return <ExpirationStatusBadge status={status} />
        },
      }),
      column.accessor('notificationTime', {
        id: VehicleColumn.NOTIFICATION_TIME,
        header: labels[VehicleColumn.NOTIFICATION_TIME],
        meta: { narrow: true },
        cell(info) {
          const { expirationDate } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !expirationDate) return '-'

          return formatDistanceStrict(
            new Date(expirationDate),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        id: VehicleColumn.RESPONSIBLE_USER,
        header: labels[VehicleColumn.RESPONSIBLE_USER],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: MENU_COLUMN_ID,
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const document = info.row.original

          const items: TableMenuItem[] = []
          if (canEdit) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(document),
            })
          }
          if (canDelete) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(document),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ]

    return showVehicle
      ? defs
      : defs.filter(def => def.id !== VehicleColumn.VEHICLE)
  }, [
    canDelete,
    canEdit,
    column,
    labels,
    onDelete,
    onEdit,
    showVehicle,
    translations.delete,
    translations.edit,
    translations.openFile,
  ])

  const columns = useMemo(
    () => resolveVehicleColumns(columnDefs, config),
    [columnDefs, config]
  )

  return (
    <DataTable
      className={cn('w-full')}
      columns={columns}
      data={documents}
      loading={loading || configLoading}
      inCard
      initialSort={[{ id: VehicleColumn.EXPIRATION_DATE, desc: false }]}
      noDataText={translations.noFiles}
      pagination={{ itemsText: n => translations.items({ n }) }}
    />
  )
}
